import { useMutation, useQuery } from "@tanstack/react-query";
import { message, Spin, Typography, Card, Modal, Tabs, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { adminLoginAsPartner } from "../../services/client";
import { getPartnersOptions } from "../../services/client/@tanstack/react-query.gen";
import { useEffect, useState } from "react";
import CreateAndRegisterPartnerForm from "./CreateAndRegisterPartnerForm";
import { ViewAsAdminToggle } from "../../components/Admin/ViewAsAdminToggle";
import LogoutButton from "./LougoutButton";
import { ControllersBoard } from "../../components/Admin/ControllersBoard";
import { IdentitiesBoard } from "../../components/Identities/IdentitiesBoard";
import { SitesBoard } from "../../components/Admin/SitesBoard";
import { TasksBoard } from "../../components/Admin/TasksBoard";
import { PricesBoard } from "../../components/Admin/PricesBoard";
import { setViewAsAdmin } from "../../util/viewAsAdmin";
import { DisableForViewer } from "../../components/common/DisableForViewer";

const { Text, Title } = Typography;

const adminTabKeys = {
  partners: "partners",
  controllers: "controllers",
  sites: "sites",
  identities: "identities",
  tasks: "tasks",
  prices: "prices",
} as const;

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [newPartnerModalVisible, setNewPartnerModalVisible] = useState(false);

  useEffect(() => {
    setViewAsAdmin(true);
  }, []);

  const tabFromUrl = searchParams.get("tab") ?? "";
  const activeTabKey =
    tabFromUrl in adminTabKeys ? tabFromUrl : adminTabKeys.partners;

  const handleTabChange = (key: string) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("tab", key);
      return next;
    });
  };

  const {
    data: partners,
    isPending,
    error,
  } = useQuery({
    ...getPartnersOptions({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin-token")}`,
      },
    }),
    throwOnError: true,
    retry: false,
  });

  const loginAsPartner = useMutation({
    mutationFn: (partnerId: string) =>
      adminLoginAsPartner({
        throwOnError: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("admin-token")}`,
          partner_id: partnerId,
        },
      }),
    throwOnError: true,
    onSuccess: ({ data }) => {
      message.success("Login successful!");
      localStorage.setItem("token", data.access_token);
      navigate("/");
    },
    onError: () => {
      message.error("Failed to login as partner");
    },
  });

  if (isPending && !error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin />
      </div>
    );
  }

  if (error || !partners) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="mx-auto w-full max-w-[1800px] px-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <Title level={3} className="!mb-0">
            Pleevi Admin
          </Title>
          <LogoutButton />
        </div>

        <Tabs
          type="card"
          activeKey={activeTabKey}
          onChange={handleTabChange}
          items={[
            {
              key: adminTabKeys.partners,
              label: "Partners",
              children: (
                <Card className="w-full">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                    <Text type="secondary">
                      Choose a partner to log in as. Your admin session stays
                      active.
                    </Text>
                    <div className="flex flex-wrap items-center gap-4">
                      <ViewAsAdminToggle />
                      <DisableForViewer>
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => setNewPartnerModalVisible(true)}
                        >
                          Create partner
                        </Button>
                      </DisableForViewer>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {partners.map((partner) => (
                      <Card
                        key={partner.partner_id}
                        size="small"
                        className="bg-white hover:cursor-pointer hover:shadow-md"
                        onClick={() => {
                          loginAsPartner.mutate(partner.partner_id);
                        }}
                      >
                        <Text className="my-0 font-bold">{partner.name}</Text>
                        <Text className="text-gray-400 my-0 block break-all">
                          {partner.partner_id}
                        </Text>
                      </Card>
                    ))}
                  </div>
                </Card>
              ),
            },
            {
              key: adminTabKeys.controllers,
              label: "Controllers",
              children: (
                <Card className="w-full">
                  <ControllersBoard partners={partners} />
                </Card>
              ),
            },
            {
              key: adminTabKeys.sites,
              label: "Sites",
              children: (
                <Card className="w-full">
                  <SitesBoard partners={partners} />
                </Card>
              ),
            },
            {
              key: adminTabKeys.identities,
              label: "Identities",
              children: (
                <Card className="w-full">
                  <IdentitiesBoard variant="admin" partners={partners} />
                </Card>
              ),
            },
            {
              key: adminTabKeys.tasks,
              label: "Tasks",
              children: (
                <Card className="w-full">
                  <TasksBoard partners={partners} />
                </Card>
              ),
            },
            {
              key: adminTabKeys.prices,
              label: "Prices",
              children: (
                <Card className="w-full">
                  <PricesBoard />
                </Card>
              ),
            },
          ]}
        />
      </div>

      <Modal
        open={newPartnerModalVisible}
        onCancel={() => setNewPartnerModalVisible(false)}
        footer={null}
      >
        <CreateAndRegisterPartnerForm />
      </Modal>
    </div>
  );
};

export default AdminPage;
